import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import rewardsHubService from "@/services/RewardsHubService.js";

// Async thunk for EFU Rewards Hub login
export const loginToRewardsHub = createAsyncThunk(
    "rewardsHub/login",
    async ({ consumerId, phone }, { rejectWithValue }) => {
        try {
            console.log("🎁 RewardsHub: Logging in with consumer_id:", consumerId, "phone:", phone);

            const response = await rewardsHubService.login(consumerId, phone);

            console.log("🎁 RewardsHub: Login successful:", response);

            if (response.success && response.data) {
                return response.data;
            } else {
                throw new Error(response.message || "Login failed");
            }
        } catch (error) {
            console.error("🎁 RewardsHub: Login error:", error);

            const errorMessage = error.response?.data?.message ||
                error.message ||
                "Failed to connect to Rewards Hub";

            // Don't show toast for initial app load failures to avoid spam
            // toast.error(`Rewards Hub: ${errorMessage}`);

            return rejectWithValue(errorMessage);
        }
    }
);

const initialState = {
    // Loading states
    loading: false,
    initialized: false,

    // User data from rewards hub
    // Keep full API response here for shape parity with backend
    apiResponse: null,

    // Convenience: top-level user object (mirrors apiResponse.data.user)
    user: null,
    accessToken: null,
    tokenType: null,
    expiresIn: null,

    // Error handling
    error: null,
    lastLoginAttempt: null,

    // User details
    userDetails: {
        id: null,
        firstName: null,
        lastName: null,
        points: 0,
        consumerId: null,
        phone: null,
        email: null,
    },
};

const rewardsHubSlice = createSlice({
    name: "rewardsHub",
    initialState,
    reducers: {
        // Clear error
        clearError: (state) => {
            state.error = null;
        },

        // Reset rewards hub state
        resetRewardsHub: (state) => {
            return initialState;
        },

        // Mark as initialized even if login failed
        setInitialized: (state) => {
            state.initialized = true;
        },

        // Update user points manually if needed
        updateUserPoints: (state, action) => {
            if (state.userDetails) {
                state.userDetails.points = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Login to Rewards Hub
            .addCase(loginToRewardsHub.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.lastLoginAttempt = new Date().toISOString();
            })
            .addCase(loginToRewardsHub.fulfilled, (state, action) => {
                state.loading = false;
                state.initialized = true;
                state.error = null;

                const { user } = action.payload;

                // Store main user data
                state.user = user;
                // Keep entire API response so callers can inspect raw shape
                state.apiResponse = action.payload;

                state.accessToken = user.access_token;
                state.tokenType = user.token_type;
                state.expiresIn = user.expires_in;

                // Store user details for easy access
                if (user.details) {
                    state.userDetails = {
                        id: user.details.id,
                        firstName: user.details.first_name,
                        lastName: user.details.last_name,
                        points: user.details.points || 0,
                        consumerId: user.details.consumer_id,
                        phone: user.details.phone,
                        email: user.email,
                    };

                    // Mirror user at top-level for easy selection (state.user)
                    state.user = user;
                }

                console.log("🎁 RewardsHub: User data stored in Redux:", state.userDetails);
            })
            .addCase(loginToRewardsHub.rejected, (state, action) => {
                state.loading = false;
                state.initialized = true; // Mark as initialized even on failure
                state.error = action.payload;
                state.user = null;
                state.apiResponse = null;
                state.accessToken = null;

                console.warn("🎁 RewardsHub: Login failed:", action.payload);
            })
            // Drop the previous user's DW session on logout so the next user gets a fresh DW login
            .addMatcher(
                (action) =>
                    action.type === "auth/logout" ||
                    action.type === "auth/logoutUser/fulfilled" ||
                    action.type === "auth/logoutUser/rejected",
                () => initialState
            );
    },
});

export const {
    clearError,
    resetRewardsHub,
    setInitialized,
    updateUserPoints
} = rewardsHubSlice.actions;

export default rewardsHubSlice.reducer;